import React, { useState, useEffect } from "react";
import {
  UserIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { analyzeNameVariations, standardizeNames } from "../utils/nameUtils";

/**
 * Is this name already usable as the standard form?
 *
 * Trailing and leading spaces count against it: a name that only differs by an invisible
 * space still reads as a separate teacher everywhere the value is compared.
 */
const isCleanForm = (name) =>
  Boolean(name) &&
  name === name.trim() &&
  !name.includes(".") &&
  !/\s{2,}/.test(name) &&
  !/^(mr|mrs|ms|dr|prof)/i.test(name);

// Helper function to suggest a clean standardized form of a name (duplicate from nameUtils)
const suggestCleanForm = (name) => {
  return name
    .replace(/^(mr\.?|mrs\.?|ms\.?|dr\.?|prof\.?)\s*/i, "") // Remove prefixes
    .replace(/\./g, "") // Remove dots
    .replace(/\s+/g, " ") // Normalize spaces
    .trim()
    .split(" ") // Split into parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()) // Proper case
    .join(" "); // Rejoin with single spaces
};

const NameResolutionSection = ({ excelData, fileName, onDataUpdated, onNotification }) => {
  const [nameAnalysis, setNameAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedNames, setSelectedNames] = useState({});
  const [isResolved, setIsResolved] = useState(false);
  const [showCompletionStatus, setShowCompletionStatus] = useState(false);

  // Reset state when new Excel data is uploaded
  useEffect(() => {
    if (excelData) {
      setNameAnalysis(null);
      setIsResolved(false);
      setSelectedNames({});
      setShowDialog(false);
      setShowCompletionStatus(false);
    }
  }, [excelData]);

  // Analyze name variations
  const analyzeNames = () => {
    if (!excelData) {
      alert("No Excel data available for analysis");
      return;
    }

    setIsAnalyzing(true);
    try {
      console.log("🔍 Starting name variation analysis...");
      const analysis = analyzeNameVariations(excelData);
      setNameAnalysis(analysis);

      // Initialize selected names with best suggestion for each group
      if (analysis.variations) {
        const initialSelections = {};
        Object.entries(analysis.variations).forEach(([key, variations]) => {
          const variationsArray = Array.from(variations);

          // Prefer a spelling that is already usable exactly as it appears in the data.
          let bestDefault = variationsArray.find(isCleanForm);

          if (!bestDefault) {
            // Nothing in the data is usable as-is, so offer a cleaned-up form.
            //
            // This used to happen only for groups with a single variant. A group where
            // EVERY variant was unclean - say "K. Bimpeh " and "K. Bimpeh", both dotted -
            // fell through to variationsArray[0] and "standardised" onto a name that was
            // still dotted and still had a trailing space. The group then came back on
            // the next analysis and only settled on a second pass.
            //
            // All variants in a group share a normalised key, so they all clean to the
            // same form; the first is as good a basis as any.
            const suggestedClean = suggestCleanForm(variationsArray[0]);

            if (suggestedClean && !variations.has(suggestedClean)) {
              variations.add(suggestedClean);
              variationsArray.push(suggestedClean);
              console.log(
                `💡 Suggesting clean form for "${variationsArray[0]}": "${suggestedClean}"`
              );
            }

            bestDefault = suggestedClean || variationsArray[0];
          }

          initialSelections[key] = bestDefault;
        });
        setSelectedNames(initialSelections);
      }

      console.log("✅ Name analysis completed:", analysis);
    } catch (error) {
      console.error("❌ Error analyzing names:", error);
      alert(`Error analyzing names: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handle name selection in dialog
  const handleNameSelect = (normalizedKey, selectedName) => {
    setSelectedNames((prev) => ({
      ...prev,
      [normalizedKey]: selectedName,
    }));
  };

  // Apply selected name standardizations
  const applyNameStandardization = () => {
    if (!excelData || !selectedNames) {
      alert("No data or selections available");
      return;
    }

    try {
      console.log("🔄 Applying name standardization...");
      const standardizedData = standardizeNames(excelData, selectedNames);

      setIsResolved(true);
      setShowDialog(false);
      setShowCompletionStatus(true);

      // Hide completion status after 3 seconds
      setTimeout(() => {
        setShowCompletionStatus(false);
      }, 3000);

      // Notify parent component about updated data
      if (onDataUpdated) {
        console.log("🚀 NameResolutionSection: Sending standardized data to parent:", {
          originalDataCount: excelData.data?.length,
          standardizedDataCount: standardizedData.data?.length,
          sampleOriginal: excelData.data?.[0],
          sampleStandardized: standardizedData.data?.[0],
        });
        onDataUpdated(standardizedData);
      }

      // Notify parent App component for main notification area
      if (onNotification) {
        onNotification(
          "Teachers' name resolution has been completed successfully! Selected variants have been applied to standardize teacher names."
        );
      }

      console.log("✅ Name standardization applied successfully");
    } catch (error) {
      console.error("❌ Error applying standardization:", error);
      alert(`Error applying standardization: ${error.message}`);
    }
  };

  // Get count of teachers with variations
  const getVariationCount = () => {
    if (!nameAnalysis || !nameAnalysis.variations) return 0;
    return Object.keys(nameAnalysis.variations).length;
  };

  // Helper function to visualize spaces in name variations
  const visualizeSpaces = (name) => {
    // Handle all types of spaces and show their character codes for debugging
    return name.replace(/[\s\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, (match) => {
      const code = match.charCodeAt(0);
      if (code === 32) return "★"; // Regular space
      if (code === 160) return "⭐"; // Non-breaking space
      return "⚫"; // Other space types
    });
  };

  return (
    <div className="name-resolution-section bg-white rounded-lg shadow-md p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <UserIcon className="h-8 w-8 text-purple-500 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Name Resolution</h2>
            <p className="text-sm text-gray-600">
              Standardize teacher name variations found in the data
            </p>
          </div>
        </div>

        {/* Once names are analysed, the results banner below carries the one Resolve button */}
        {!nameAnalysis && !isResolved && excelData && (
          <button
            onClick={analyzeNames}
            disabled={isAnalyzing}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {isAnalyzing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Analyzing...
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                Analyze Names
              </>
            )}
          </button>
        )}
      </div>

      {/* Completion Status Notification */}
      {showCompletionStatus && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 animate-pulse">
          <div className="flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
            <div>
              <h3 className="font-semibold text-green-900">Selected Variants Applied!</h3>
              <p className="text-sm text-green-700">
                Teacher names have been successfully standardized using your selected variants.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {isResolved && !showCompletionStatus && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
            <div>
              <h3 className="font-semibold text-green-900">Name Resolution Complete</h3>
              <p className="text-sm text-green-700">
                Teacher names have been standardized. Upload a new Excel file to resolve names
                again.
              </p>
            </div>
          </div>
        </div>
      )}

      {nameAnalysis && !isResolved && getVariationCount() > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-blue-500 mr-3" />
              <div>
                <h3 className="font-semibold text-blue-900">Name Variations Found</h3>
                <p className="text-sm text-blue-700">
                  {getVariationCount()} teachers have name variations that need resolution
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDialog(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Resolve Names
            </button>
          </div>
        </div>
      )}

      {nameAnalysis && !isResolved && getVariationCount() === 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
            <h3 className="font-semibold text-green-900">No Name Variations Found</h3>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!excelData && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <UserIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Excel Data Available</h3>
          <p className="text-gray-600">
            Please upload an Excel file first to analyze teacher name variations.
          </p>
        </div>
      )}

      {/* Name Resolution Dialog */}
      {showDialog && nameAnalysis && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6">
            <div className="flex items-start justify-between border-b pb-3">
              <div className="flex items-center">
                <UserIcon className="h-6 w-6 text-purple-500 mr-2" />
                <h3 className="text-xl font-semibold text-gray-900">Standardize Teacher Names</h3>
              </div>
              <button
                onClick={() => setShowDialog(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Legend */}
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <h4 className="font-medium text-blue-900">Legends:</h4>
                <div className="flex items-center text-sm text-blue-700">
                  <span className="font-bold text-red-600">★</span>
                  <span className="ml-1">=</span>
                  <span className="ml-1 font-medium">SPACE</span>
                </div>
              </div>
            </div>

            <div className="mt-4 max-h-96 overflow-y-auto">
              {Object.keys(nameAnalysis.variations).length === 0 ? (
                <div className="text-center py-8 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-green-900 mb-2">
                    No Name Variations Found!
                  </h3>
                  <p className="text-green-700">
                    All teacher names are already standardized. No action needed.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(nameAnalysis.variations).map(([normalizedKey, variations]) => (
                    <div key={normalizedKey} className="bg-gray-50 p-4 rounded-lg">
                      <div className="font-medium text-gray-700 mb-2">Name Variations Found:</div>
                      <div className="grid grid-cols-1 gap-2">
                        {Array.from(variations).map((variation) => (
                          <label
                            key={variation}
                            className="flex items-center space-x-3 p-3 hover:bg-gray-100 rounded cursor-pointer border border-gray-200"
                          >
                            <input
                              type="radio"
                              name={`name-${normalizedKey}`}
                              value={variation}
                              checked={selectedNames[normalizedKey] === variation}
                              onChange={(e) => handleNameSelect(normalizedKey, e.target.value)}
                              className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="text-gray-900 font-medium">{variation}</div>
                              <div className="text-sm text-red-600 font-mono">
                                ({visualizeSpaces(variation)})
                              </div>
                            </div>
                            {selectedNames[normalizedKey] === variation && (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {Object.keys(nameAnalysis.variations).length > 0 && (
              <div className="mt-6 flex justify-end space-x-3 border-t pt-4">
                <button
                  onClick={() => setShowDialog(false)}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={applyNameStandardization}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors duration-200"
                >
                  Apply Selected Variants
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NameResolutionSection;
